import React, { useEffect, useState } from 'react'
import { Container, Row, Col, Form, Button, Table, Badge } from 'react-bootstrap'
import Card from '../../../components/Card'
import { useGetGhlAuthMutation, useSyncGhlUsersMutation, useUpdateGhlUserMutation, useUpdateGhlAuthMutation } from '../../../api/ghlAuthsAPISlices'
import { useGetAllMlmAccountsMutation } from '../../../api/mlmAccountsAPISlices'
import { useNavigate, useParams } from 'react-router-dom'
import Toaster from '../../../components/utility/Toaster'

const SubaccountsEdit = () => {
    const { subaccountId } = useParams();
    const navigate = useNavigate();
    const [subaccount, setSubaccount] = useState(null);
    const [ghlUsers, setGhlUsers] = useState([]);
    const [isFetching, setIsFetching] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);

    const [showToster, setShowToster] = useState(false);
    const [tosterMassage, setTosterMassage] = useState('');
    const [tosterStatus, setTosterStatus] = useState('');

    const authToken = JSON.parse(localStorage.getItem("token"));

    const [mlmAccounts, setMlmAccounts] = useState([]);
    const [selectedMlmAccountId, setSelectedMlmAccountId] = useState('');
    const [isSavingMlm, setIsSavingMlm] = useState(false);

    const [getGhlAuth] = useGetGhlAuthMutation();
    const [syncGhlUsers] = useSyncGhlUsersMutation();
    const [updateGhlUser] = useUpdateGhlUserMutation();
    const [updateGhlAuth] = useUpdateGhlAuthMutation();
    const [getAllMlmAccounts] = useGetAllMlmAccountsMutation();

    useEffect(() => {
        fetchSubaccountData();
        fetchMlmAccounts();
    }, [subaccountId])

    const fetchSubaccountData = async () => {
        setIsFetching(true);
        await getGhlAuth({ ghlAuthId: subaccountId, token: authToken })
            .unwrap()
            .then((res) => {
                const data = res.data || res;
                setSubaccount(data);
                setGhlUsers(data.ghl_users || []);
                setSelectedMlmAccountId(data.mlm_account_id ? String(data.mlm_account_id) : '');
                setIsFetching(false);
            })
            .catch((error) => {
                setShowToster(true)
                setTosterMassage(error?.data?.message || 'Error loading subaccount')
                setTosterStatus('error')
                setIsFetching(false);
            });
    };

    const fetchMlmAccounts = async () => {
        await getAllMlmAccounts({ token: authToken })
            .unwrap()
            .then((res) => {
                const list = res.data || res;
                setMlmAccounts(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                // Silently fail — not critical
            });
    };

    const handleSaveMlmAccount = async () => {
        setIsSavingMlm(true);
        await updateGhlAuth({
            ghlAuthId: subaccountId,
            payload: { mlm_account_id: selectedMlmAccountId || null },
            token: authToken
        })
            .unwrap()
            .then(() => {
                setShowToster(true);
                setTosterMassage('MLM Account updated successfully');
                setTosterStatus('success');
            })
            .catch((error) => {
                setShowToster(true);
                setTosterMassage(error?.data?.message || 'Error updating MLM account');
                setTosterStatus('error');
            })
            .finally(() => setIsSavingMlm(false));
    };

    const handleSyncUsers = async () => {
        setIsSyncing(true);
        await syncGhlUsers({ ghlAuthId: subaccountId, token: authToken })
            .unwrap()
            .then((res) => {
                setGhlUsers(res.ghl_auth?.ghl_users || []);
                setShowToster(true);
                setTosterMassage(`Synced ${res.synced_count} GHL users`);
                setTosterStatus('success');
            })
            .catch((error) => {
                setShowToster(true);
                setTosterMassage(error?.data?.message || 'Error syncing users');
                setTosterStatus('error');
            })
            .finally(() => setIsSyncing(false));
    };

    const handleUpdateGhlUser = async (ghlUserId, mlmLoLogin) => {
        await updateGhlUser({
            ghlUserId,
            payload: { mlm_lo_login: mlmLoLogin },
            token: authToken
        })
            .unwrap()
            .then(() => {
                setGhlUsers(prev => prev.map(u =>
                    u.id === ghlUserId ? { ...u, mlm_lo_login: mlmLoLogin } : u
                ));
                setShowToster(true);
                setTosterMassage('GHL user updated');
                setTosterStatus('success');
            })
            .catch((error) => {
                setShowToster(true);
                setTosterMassage(error?.data?.message || 'Error updating user');
                setTosterStatus('error');
            });
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    if (isFetching) {
        return (
            <Container fluid>
                <div className='text-center mt-5 pt-5'>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading subaccount details...</p>
                </div>
            </Container>
        );
    }

    return (
        <>
            <Container fluid>
                {/* Header with Back Button */}
                <Row className="mb-4">
                    <Col>
                        <div className="d-flex align-items-center gap-3">
                            <Button
                                variant="light"
                                className="d-flex align-items-center gap-2"
                                onClick={() => navigate("/subaccounts-list")}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back
                            </Button>
                            <div>
                                <h4 className="mb-0">Subaccount Details</h4>
                                <small className="text-muted">View and manage GHL subaccount information</small>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Info Cards */}
                <Row className="mb-4">
                    <Col md="6" lg="3" className="mb-3">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-start gap-3">
                                    <div className="rounded-3 p-2 bg-secondary bg-opacity-10">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-secondary">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                    </div>
                                    <div className="flex-grow-1">
                                        <p className="text-muted small mb-1">Business Name</p>
                                        <p className="mb-0 fw-medium">
                                            {subaccount?.business_name || <span className="text-muted fst-italic">Not set</span>}
                                        </p>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md="6" lg="3" className="mb-3">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-start gap-3">
                                    <div className="rounded-3 p-2 bg-primary bg-opacity-10">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-primary">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <div className="flex-grow-1 min-w-0">
                                        <p className="text-muted small mb-1">Location ID</p>
                                        <code className="text-primary small d-block text-truncate" title={subaccount?.locationId}>
                                            {subaccount?.locationId}
                                        </code>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md="6" lg="3" className="mb-3">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-start gap-3">
                                    <div className="rounded-3 p-2 bg-success bg-opacity-10">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-success">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                    </div>
                                    <div className="flex-grow-1">
                                        <p className="text-muted small mb-1">Associated Users</p>
                                        <p className="mb-0 fw-medium">
                                            {subaccount?.users?.length > 0
                                                ? subaccount.users.map(u => u.name).join(', ')
                                                : <span className="text-muted fst-italic">None assigned</span>
                                            }
                                        </p>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md="6" lg="3" className="mb-3">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-start gap-3">
                                    <div className="rounded-3 p-2 bg-warning bg-opacity-10">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-warning">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div className="flex-grow-1">
                                        <p className="text-muted small mb-1">Last Synced</p>
                                        <p className="mb-0 fw-medium small">{formatDate(subaccount?.updated_at)}</p>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* MLM Account Section */}
                <Row className="mb-4">
                    <Col lg="6">
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <h6 className="mb-3">MLM Account</h6>
                                <div className="d-flex align-items-center gap-3">
                                    <select
                                        className="form-select"
                                        value={selectedMlmAccountId}
                                        onChange={(e) => setSelectedMlmAccountId(e.target.value)}
                                        style={{ maxWidth: 300 }}
                                    >
                                        <option value="">— None —</option>
                                        {mlmAccounts.map((acct) => (
                                            <option key={acct.id} value={String(acct.id)}>
                                                {acct.name}
                                            </option>
                                        ))}
                                    </select>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleSaveMlmAccount}
                                        disabled={isSavingMlm}
                                    >
                                        {isSavingMlm ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Saving...
                                            </>
                                        ) : 'Save'}
                                    </Button>
                                </div>
                                <small className="text-muted mt-2 d-block">
                                    Link this subaccount to an MLM account for webhook processing.
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* GHL Users Section */}
                <Row>
                    <Col lg="12">
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom py-3">
                                <div className="d-flex justify-content-between align-items-center w-100">
                                    <div>
                                        <h5 className="mb-1">GHL Users</h5>
                                        <small className="text-muted">Manage user mappings for this location</small>
                                    </div>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleSyncUsers}
                                        disabled={isSyncing}
                                        className="d-flex align-items-center gap-2 ms-auto"
                                    >
                                        {isSyncing ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                Syncing...
                                            </>
                                        ) : (
                                            <>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Sync Users
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Card.Header>

                            <Card.Body className="p-0">
                                {ghlUsers.length === 0 ? (
                                    <div className="text-center py-5">
                                        <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 64, height: 64 }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-muted">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                        </div>
                                        <h6 className="text-muted mb-2">No GHL Users Found</h6>
                                        <p className="text-muted small mb-0">Click "Sync Users" to fetch users from GoHighLevel</p>
                                    </div>
                                ) : (
                                    <Table responsive hover className="mb-0 align-middle">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="border-0 ps-4 py-3">Name</th>
                                                <th className="border-0 py-3">GHL User ID</th>
                                                <th className="border-0 py-3">MLM LO Login</th>
                                                <th className="border-0 py-3 pe-4 text-end" style={{ width: '140px' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ghlUsers.map(ghlUser => (
                                                <GhlUserRow
                                                    key={ghlUser.id}
                                                    ghlUser={ghlUser}
                                                    onUpdate={handleUpdateGhlUser}
                                                />
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Toaster status={tosterStatus} message={tosterMassage} show={showToster} setShow={setShowToster} />
        </>
    )
}

// Inline editable row for GHL users
const GhlUserRow = ({ ghlUser, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [mlmLoLogin, setMlmLoLogin] = useState(ghlUser.mlm_lo_login || '');
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        await onUpdate(ghlUser.id, mlmLoLogin);
        setIsSaving(false);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setMlmLoLogin(ghlUser.mlm_lo_login || '');
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    return (
        <tr>
            <td className="ps-4 py-3">
                <div className="d-flex align-items-center gap-2">
                    <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                        <span className="text-primary fw-medium">{(ghlUser.name || 'U').charAt(0).toUpperCase()}</span>
                    </div>
                    <span className="fw-medium">{ghlUser.name || 'N/A'}</span>
                </div>
            </td>
            <td className="py-3">
                <code className="small bg-light px-2 py-1 rounded">{ghlUser.ghl_user_id}</code>
            </td>
            <td className="py-3">
                {isEditing ? (
                    <Form.Control
                        type="text"
                        size="sm"
                        value={mlmLoLogin}
                        onChange={(e) => setMlmLoLogin(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Enter MLM LO login"
                        autoFocus
                        style={{ maxWidth: 200 }}
                    />
                ) : (
                    ghlUser.mlm_lo_login
                        ? <Badge bg="light" text="dark" className="fw-normal px-2 py-1">{ghlUser.mlm_lo_login}</Badge>
                        : <span className="text-muted fst-italic small">Not set</span>
                )}
            </td>
            <td className="py-3 pe-4 text-end">
                {isEditing ? (
                    <div className="d-flex gap-1 justify-content-end">
                        <Button
                            variant="success"
                            size="sm"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleCancel}
                            disabled={isSaving}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </Button>
                    </div>
                ) : (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setIsEditing(true)}
                        className="d-flex align-items-center gap-1"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Edit
                    </Button>
                )}
            </td>
        </tr>
    );
};

export default SubaccountsEdit
